/**
 * Finding a VIN in whatever text was read off a photo of the plate.
 *
 * A VIN is 17 characters of capitals and digits that never include I, O or Q,
 * so the letters an OCR confuses with digits can be put right. North American
 * VINs carry a check digit in ninth place; a candidate that passes it is
 * trusted, one that does not (most European ones) is offered for checking.
 */

export const VIN_LENGTH = 17;
const VIN_CHARS = /^[A-HJ-NPR-Z0-9]{17}$/;

/** I, O and Q are never in a VIN; on a plate they are a 1 or a 0 misread. */
const MISREAD: Record<string, string> = { O: '0', Q: '0', I: '1' };

const VALUES: Record<string, number> = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, J: 1, K: 2, L: 3, M: 4, N: 5,
  P: 7, R: 9, S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
};
const WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

/** Capitals and digits only, with the letters a VIN cannot hold turned back into digits. */
export function cleanVin(text: string): string {
  return text.toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/[OQI]/g, (c) => MISREAD[c]!);
}

export const isVinShaped = (vin: string): boolean => VIN_CHARS.test(vin);

/**
 * Whether seventeen characters could be a real VIN rather than a misread:
 * the maker's code in the first three always has a letter, the serial number
 * at the end finishes in four digits, and no character fills half the number —
 * what a reader produces when it sees a pattern, not text.
 */
export function isPlausibleVin(vin: string): boolean {
  if (!isVinShaped(vin) || !/[A-Z]/.test(vin.slice(0, 3))) return false;
  // The serial number ends the VIN, and its last four places are digits.
  if (!/^\d{4}$/.test(vin.slice(13))) return false;
  // Words read off the plate around the VIN are mostly letters; a VIN never is.
  if ((vin.match(/[A-Z]/g) ?? []).length > 11) return false;
  const counts = new Map<string, number>();
  for (const char of vin) counts.set(char, (counts.get(char) ?? 0) + 1);
  return Math.max(...counts.values()) <= 7;
}

/** Whether the ninth character is the check digit the other sixteen add up to. */
export function hasValidCheckDigit(vin: string): boolean {
  if (!isVinShaped(vin)) return false;
  const sum = [...vin].reduce((total, char, i) => {
    const value = /\d/.test(char) ? Number(char) : VALUES[char] ?? 0;
    return total + value * WEIGHTS[i]!;
  }, 0);
  const check = sum % 11;
  return vin[8] === (check === 10 ? 'X' : String(check));
}

export interface FoundVin {
  vin: string;
  /** Passed the check digit: almost certainly read right. */
  verified: boolean;
  /** Read from a barcode, which leaves no room for a misread character. */
  fromBarcode?: boolean;
}

/**
 * The most likely VIN in OCR output, or null. Each line is tried with its
 * spaces closed up — plates often print the VIN in groups — and every
 * 17-character stretch of it is a candidate; one that passes the check digit
 * wins, then one with both letters and digits, as every real VIN has.
 */
export function findVin(text: string): FoundVin | null {
  const candidates: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    const joined = cleanVin(line);
    for (let i = 0; i + VIN_LENGTH <= joined.length; i += 1) {
      const slice = joined.slice(i, i + VIN_LENGTH);
      if (isPlausibleVin(slice)) candidates.push(slice);
    }
  }
  if (candidates.length === 0) return null;

  const verified = candidates.find(hasValidCheckDigit);
  if (verified) return { vin: verified, verified: true };
  const mixed = candidates.find((c) => /[A-Z]/.test(c) && /\d/.test(c));
  return { vin: mixed ?? candidates[0]!, verified: false };
}

export interface Box { left: number; top: number; width: number; height: number }

/**
 * Where an on-screen frame falls in the video's own pixels, for a video that
 * fills its box and is cut to fit (object-fit: cover): screen and picture
 * differ by one scale and an offset on the side that was cut.
 */
export function frameInVideo(screen: Box, frame: Box, videoWidth: number, videoHeight: number): Box {
  const scale = Math.max(screen.width / videoWidth, screen.height / videoHeight);
  const offsetX = (videoWidth * scale - screen.width) / 2;
  const offsetY = (videoHeight * scale - screen.height) / 2;
  return {
    left: (frame.left - screen.left + offsetX) / scale,
    top: (frame.top - screen.top + offsetY) / scale,
    width: frame.width / scale,
    height: frame.height / scale,
  };
}
